import {dropLast, equals, gt, gte, includes, isEmpty, join, length, map, pathOr, replace, split, trim,} from 'ramda';
import {BUSINESS_CITY, BUSINESS_STATE, BUSINESS_STREET, BUSINESS_ZIPCODE,} from 'src/services/constants';

import {DONT_ALLOW_EIN_SEQ} from 'src/utilities/constants';
import {alphaRegex, numbericRegex, webSiteRegex} from 'src/utilities/regex';

const tinRegexFormat = /^\d{2}-\d{1,10}$/;

const SSN_REGEX = /^(?!(000|666|9))\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/;
const TIN_REGEX = /^\d{2}-\d{6}|\d{7}$/;
const EIN_REGEX = /^\d{2}-\d{6-10}$/;

export const phoneKey = (index: number) => `owner_${index + 1}_phone_number`;
export const secPhoneKey = `owner_secodary_phone_number`;
export const phoneNumberKey = `applicant_phone_number`;
export const mortgagePaymentKey = (index: number) =>
  `owner_${index + 1}_mortgage_payment`;
export const emailIdKey = (index: number) => `owner_${index + 1}_email_id`;
export const rentPaidKey = (index: number) => `owner_${index + 1}_rent_paid`;
export const workFlowSection = (index: number) => `owner${index + 1}Business`;
export const ownersOwnOrRentKey = (index: number) =>
  `owner_${index + 1}_own_or_rent`;

export const ownerDob = (index: number) => `owner_${index + 1}_dob`;
export const ownerDrivingLicence = (index: number) =>
  `owner_${index + 1}_driving_licence_number`;
export const validatorTinOrEin = (data: any) => {
  const is_valid = TIN_REGEX.test(data) || EIN_REGEX.test(data);
  return {
    isValid: is_valid,
    reason: is_valid ? '' : 'Enter valid TIN (or) EIN (eg: 222-11-3431)',
  };
};
export const websiteValidation = (data: any) => {
  const is_valid = webSiteRegex.test(data);
  return is_valid ? '' : 'Enter valid website (eg: www.website.com)'
};
export const validatorEin = (data: any) => {
  return (
    tinRegexFormat.test(data) &&
    !checkTrend(data) &&
    equals(length(deNormalizeEIN(data) as any), 9)
  );
};
export const validatorSsn = (data: any) => {
  return isSnn(data) && !checkTrend(data);
};
export const checkRepeat = (number: string) => {
  return /^\D*(\d)(?:\D*|\1)*$/.test(number);
};

export const deNormalizeEIN = (ein: string) =>
  join(
    '',
    map((x) => replace('-', '', x), split('', ein))
  );

export const checkTrend = (ein: string) => {
  let einNum: any = deNormalizeEIN(ein);
  const extraDigits = length(einNum) - 9;
  if (gt(extraDigits, 0)) {
    einNum = dropLast(extraDigits, einNum);
  }
  if (gte(length(einNum), 9)) {
    return checkRepeat(ein) || includes(einNum, DONT_ALLOW_EIN_SEQ);
  } else {
    return false;
  }
};

export const validatorTinOrSin = (data: any) => {
  const is_valid = isTinOrEinOrSnn(data);
  return {
    isValid: is_valid,
    reason: is_valid ? '' : 'Enter valid EIN/TIN or SSN',
  };
};
export const validatorSSN = (data: any) => {
  const is_valid = isSnn(data);
  return {
    isValid: is_valid,
    reason: is_valid ? '' : 'Enter valid SSN#',
  };
};
export const validatorTin = (data: any) => {
  const is_valid = isEin(data);
  return {
    isValid: is_valid,
    reason: is_valid ? '' : 'Enter valid EIN/TIN#',
  };
};

const isSnn = (data: any) => {
  return SSN_REGEX.test(data) && !checkTrend(data);
};
const isEin = (data: any) => {
  return EIN_REGEX.test(data) || TIN_REGEX.test(data);
};
const isTinOrEinOrSnn = (data: any) => {
  return SSN_REGEX.test(data) || TIN_REGEX.test(data) || EIN_REGEX.test(data);
};

export const numericFormattor = (number: any) => {
  return numbericRegex.test(number) ? number : undefined;
};

export const alphaFormattor = (value: any) => {
  return alphaRegex.test(value) ? value : undefined;
};

export const routingValidation = (number: any) => {
  return numbericRegex.test(number) && number.length <= 9 ? number : undefined;
};

export const accountValidation = (number: any) => {
  return numbericRegex.test(number) && number.length <= 12 ? number : undefined;
};

export const routingNumberFormatter = (value: any) => {
  if (isEmpty(trim(value))) {
    return '';
  }
  return routingValidation(value);
};

export const accountFormattor = (value: any) => {
  if (isEmpty(trim(value))) {
    return '';
  }
  return accountValidation(value);
};

export const serperatedBusinessAddress = (address: any) => {
  const street = pathOr('', [BUSINESS_STREET], address);
  const city = pathOr('', [BUSINESS_CITY], address);
  const state = pathOr('', [BUSINESS_STATE], address);
  const stateWithZipCode = join(' ', [
    state,
    pathOr('', [BUSINESS_ZIPCODE], address),
  ]);
  return { street, city, stateWithZipCode };
};

export const stringifyAddress = (address: any, metaData: any) => {
  if (isEmpty(address) && !isEmpty(pathOr('', [BUSINESS_STREET], metaData))) {
    const { street, city, stateWithZipCode } =
      serperatedBusinessAddress(metaData);
    return join(', ', [street, city, stateWithZipCode]);
  }
  return address;
};
